/**
 * User Profile Component
 * Renders a user's public profile with their sessions
 */

import { WebUserProfile, WebPublicSession, formatDuration, formatDate } from '../api/firebase';

/**
 * Render the user profile header with avatar and basic info
 */
export function renderProfileHeader(profile: WebUserProfile, container: HTMLElement): void {
  const locationText = profile.location
    ? [profile.location.locality, profile.location.country].filter(Boolean).join(', ')
    : null;

  const initials = profile.displayName
    ? profile.displayName.split(' ').map(n => n[0]).join('').toUpperCase().substring(0, 2)
    : '?';

  container.innerHTML = `
    <div class="profile-header">
      <div class="profile-avatar-container">
        ${profile.photoURL
          ? `<img src="${escapeHtml(profile.photoURL)}" alt="${escapeHtml(profile.displayName)}" class="profile-avatar" />`
          : `<div class="profile-avatar profile-avatar-initials">${escapeHtml(initials)}</div>`
        }
        ${profile.isDJ ? '<div class="profile-dj-badge">DJ</div>' : ''}
      </div>

      <h1 class="profile-name">${escapeHtml(profile.displayName)}</h1>

      ${profile.djProfile?.stageName
        ? `<p class="profile-stage-name">"${escapeHtml(profile.djProfile.stageName)}"</p>`
        : ''
      }

      ${profile.anonymizedEmail
        ? `<p class="profile-email">${escapeHtml(profile.anonymizedEmail)}</p>`
        : ''
      }

      ${locationText
        ? `<p class="profile-location"><span class="location-icon">📍</span> ${escapeHtml(locationText)}</p>`
        : ''
      }

      ${profile.bio
        ? `<p class="profile-bio">${escapeHtml(profile.bio)}</p>`
        : ''
      }
    </div>
  `;
}

/**
 * Render profile statistics
 */
export function renderProfileStats(profile: WebUserProfile, container: HTMLElement): void {
  container.innerHTML = `
    <div class="profile-stats">
      <div class="profile-stat">
        <div class="profile-stat-value">${profile.sessionCount.toLocaleString()}</div>
        <div class="profile-stat-label">Total Sessions</div>
      </div>
      <div class="profile-stat">
        <div class="profile-stat-value">${profile.publicSessionCount.toLocaleString()}</div>
        <div class="profile-stat-label">Public Sessions</div>
      </div>
      <div class="profile-stat">
        <div class="profile-stat-value">${formatBits(profile.totalBitsGenerated)}</div>
        <div class="profile-stat-label">Bits Generated</div>
      </div>
    </div>
  `;
}

/**
 * Render DJ profile section
 */
export function renderDJProfile(profile: WebUserProfile, container: HTMLElement): void {
  if (!profile.isDJ || !profile.djProfile) {
    container.innerHTML = '';
    return;
  }

  const dj = profile.djProfile;
  const socialLinks = dj.socialLinks || {};
  const hasSocialLinks = Object.values(socialLinks).some(Boolean);

  container.innerHTML = `
    <div class="card profile-dj-section">
      <h2 class="profile-section-title">DJ Profile</h2>

      ${dj.genres && dj.genres.length > 0
        ? `
          <div class="profile-dj-genres">
            ${dj.genres.map(genre => `<span class="genre-tag">${escapeHtml(genre)}</span>`).join('')}
          </div>
        `
        : ''
      }

      ${dj.experience
        ? `<p class="profile-dj-experience"><strong>Experience:</strong> ${escapeHtml(dj.experience)}</p>`
        : ''
      }

      ${dj.availableForBooking
        ? `<div class="profile-booking-badge">✓ Available for Booking</div>`
        : ''
      }

      ${hasSocialLinks
        ? `
          <div class="profile-social-links">
            ${socialLinks.website ? `<a href="${escapeHtml(socialLinks.website)}" target="_blank" rel="noopener" class="social-link">🌐 Website</a>` : ''}
            ${socialLinks.soundcloud ? `<a href="${escapeHtml(socialLinks.soundcloud)}" target="_blank" rel="noopener" class="social-link">☁️ SoundCloud</a>` : ''}
            ${socialLinks.mixcloud ? `<a href="${escapeHtml(socialLinks.mixcloud)}" target="_blank" rel="noopener" class="social-link">🎧 Mixcloud</a>` : ''}
            ${socialLinks.spotify ? `<a href="${escapeHtml(socialLinks.spotify)}" target="_blank" rel="noopener" class="social-link">🎵 Spotify</a>` : ''}
            ${socialLinks.instagram ? `<a href="${escapeHtml(socialLinks.instagram)}" target="_blank" rel="noopener" class="social-link">📸 Instagram</a>` : ''}
          </div>
        `
        : ''
      }
    </div>
  `;
}

/**
 * Render public sessions list
 */
export function renderProfileSessions(sessions: WebPublicSession[], container: HTMLElement): void {
  if (sessions.length === 0) {
    container.innerHTML = `
      <div class="card profile-sessions-section">
        <h2 class="profile-section-title">Public Sessions</h2>
        <p class="no-sessions-message">No public sessions yet</p>
      </div>
    `;
    return;
  }

  container.innerHTML = `
    <div class="card profile-sessions-section">
      <h2 class="profile-section-title">Public Sessions</h2>
      <div class="profile-sessions-list">
        ${sessions.map(session => renderSessionCard(session)).join('')}
      </div>
    </div>
  `;
}

/**
 * Render a single session card
 */
function renderSessionCard(session: WebPublicSession): string {
  const stats = session.statistics;
  const significanceClass = stats ? getSignificanceClass(stats.significance) : '';

  return `
    <a href="/${escapeHtml(session.shareId)}" class="session-card-link">
      <div class="session-card">
        <div class="session-card-header">
          <h3 class="session-card-title">${escapeHtml(session.title)}</h3>
          <span class="session-card-duration">${formatDuration(session.duration)}</span>
        </div>

        <div class="session-card-meta">
          <span class="session-card-date">${formatDate(session.startTime)}</span>
          ${session.location ? `<span class="session-card-location">📍 ${escapeHtml(session.location)}</span>` : ''}
        </div>

        ${stats
          ? `
            <div class="session-card-stats">
              <span class="session-card-bits">${stats.totalBits.toLocaleString()} bits</span>
              <span class="session-card-zscore ${significanceClass}">Z: ${stats.zScore.toFixed(2)}</span>
            </div>
          `
          : ''
        }
      </div>
    </a>
  `;
}

/**
 * Get CSS class for significance level
 */
function getSignificanceClass(significance: string): string {
  switch (significance) {
    case 'highly_significant':
    case 'very_significant':
      return 'significance-high';
    case 'significant':
      return 'significance-medium';
    case 'marginally_significant':
      return 'significance-low';
    default:
      return '';
  }
}

/**
 * Format large bit counts
 */
function formatBits(bits: number): string {
  if (bits >= 1000000000) {
    return `${(bits / 1000000000).toFixed(1)}B`;
  }
  if (bits >= 1000000) {
    return `${(bits / 1000000).toFixed(1)}M`;
  }
  if (bits >= 1000) {
    return `${(bits / 1000).toFixed(1)}K`;
  }
  return bits.toString();
}

/**
 * Escape HTML to prevent XSS
 */
function escapeHtml(text: string): string {
  const div = document.createElement('div');
  div.textContent = text;
  return div.innerHTML;
}
